import React, { useState, useEffect } from 'react';
import { ownerAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Banknote, TrendingUp, Calendar, Loader2, Wallet, Percent } from 'lucide-react';

export default function OwnerEarnings() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadEarnings();
  }, []);

  const loadEarnings = async () => {
    try {
      const response = await ownerAPI.getEarnings();
      setData(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Einnahmen');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-tunis-blue">Einnahmen & Guthaben</h1>
          <p className="text-muted-foreground">Übersicht Ihrer Verdienste</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-tunis-teal to-tunis-teal/80 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Guthaben</p>
                  <p className="text-3xl font-bold">{(data?.wallet_balance || 0).toFixed(2)}</p>
                  <p className="text-sm opacity-80">TND</p>
                </div>
                <Wallet className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gesamteinnahmen</p>
                  <p className="text-2xl font-bold text-green-600">{(data?.total_earnings || 0).toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">TND</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Provision gezahlt</p>
                  <p className="text-2xl font-bold text-orange-600">{(data?.total_commission || 0).toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">TND</p>
                </div>
                <Percent className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Buchungen</p>
                  <p className="text-2xl font-bold text-tunis-blue">{data?.completed_bookings || 0}</p>
                  <p className="text-sm text-muted-foreground">abgeschlossen</p>
                </div>
                <Calendar className="w-8 h-8 text-tunis-blue" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Info */}
        <Card className="bg-tunis-blue/5 border-tunis-blue/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-tunis-blue/10 rounded-full">
                <Percent className="w-6 h-6 text-tunis-blue" />
              </div>
              <div>
                <p className="font-semibold">Ihre Provisionsrate: {((data?.commission_rate || 0.1) * 100).toFixed(0)}%</p>
                <p className="text-sm text-muted-foreground">
                  Bei jeder Buchung erhalten Sie {(100 - (data?.commission_rate || 0.1) * 100).toFixed(0)}% des Buchungspreises.
                  Die Plattform behält {((data?.commission_rate || 0.1) * 100).toFixed(0)}% als Servicegebühr.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Breakdown */}
        {data?.monthly_breakdown && Object.keys(data.monthly_breakdown).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Monatliche Übersicht
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(data.monthly_breakdown)
                  .sort((a, b) => b[0].localeCompare(a[0]))
                  .map(([month, stats]) => (
                    <div key={month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{month}</p>
                        <p className="text-sm text-muted-foreground">{stats.bookings} Buchungen</p>
                      </div>
                      <p className="text-lg font-bold text-tunis-teal">{(stats.earnings || 0).toFixed(2)} TND</p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Earnings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5" />
              Letzte Einnahmen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.recent_earnings?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_earnings.map((earning) => (
                  <div key={earning.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{earning.field_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {earning.date} | {earning.start_time} - {earning.end_time}
                      </p>
                      <p className="text-sm text-muted-foreground">Gebucht von: {earning.user_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-tunis-teal">+{(earning.owner_earnings || 0).toFixed(2)} TND</p>
                      <p className="text-xs text-muted-foreground">von {(earning.total_price || 0).toFixed(2)} TND</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">Noch keine abgeschlossenen Buchungen</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
